import { clsx } from 'clsx'

export interface LegendEntry {
  hex: string
  label: string
}

interface MapLegendProps {
  entries: LegendEntry[]
  /** Optional heading — omit for a pure swatch row. */
  title?: string
  /** Render as a floating absolute-positioned panel over a map, vs. an inline row. */
  floating?: boolean
  /** Corner for `floating` mode. Default: bottom-left. */
  corner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  className?: string
}

/**
 * Shared legend strip for all map/globe surfaces so "what does that colour
 * mean?" never requires guessing. Two modes:
 *
 *   inline — sits under the page heading, compact horizontal strip.
 *   floating — anchored over a map with backdrop blur, for full-bleed views.
 *
 * Tip: keep entry counts ≤6 per legend. Longer ones stop scanning well.
 */
export default function MapLegend({
  entries,
  title,
  floating = false,
  corner = 'bottom-left',
  className,
}: MapLegendProps) {
  const inner = (
    <>
      {title && (
        <div className="text-[9px] uppercase tracking-widest text-white/60 mb-1">{title}</div>
      )}
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {entries.map(e => (
          <div key={e.label} className="flex items-center gap-1.5 text-[10px] text-white/85">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: e.hex }} />
            {e.label}
          </div>
        ))}
      </div>
    </>
  )

  if (!floating) {
    return (
      <div className={clsx('card p-2 text-white/85', className)}>
        {inner}
      </div>
    )
  }

  const cornerCls =
    corner === 'top-left' ? 'top-2 left-2' :
    corner === 'top-right' ? 'top-2 right-2' :
    corner === 'bottom-right' ? 'bottom-2 right-2' :
    'bottom-2 left-2'

  return (
    <div className={clsx(
      'absolute z-[450] rounded-md p-2 bg-black/70 ring-1 ring-white/10 backdrop-blur-md pointer-events-none',
      cornerCls,
      className,
    )}>
      {inner}
    </div>
  )
}
